import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { errorLog } from '../utils/log-helper'

const ANON_ID_KEY = 'anon_id'

let cachedAnonId: string | null = null

function generateId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    // fall through to manual id
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random()
    .toString(36)
    .slice(2)}`
}

async function readStored(): Promise<string | null> {
  try {
    if (Capacitor.isNativePlatform()) {
      const { value } = await Preferences.get({ key: ANON_ID_KEY })
      return value ?? null
    }
    return localStorage.getItem(ANON_ID_KEY)
  } catch (error) {
    errorLog('[anonId] read failed', error)
    return null
  }
}

async function writeStored(id: string): Promise<void> {
  try {
    if (Capacitor.isNativePlatform()) {
      await Preferences.set({ key: ANON_ID_KEY, value: id })
    } else {
      localStorage.setItem(ANON_ID_KEY, id)
    }
  } catch (error) {
    errorLog('[anonId] write failed', error)
  }
}

/**
 * Best-effort synchronous read of the anon id (web localStorage / in-memory cache).
 *
 * Returns null if not yet available (e.g. native platform before `ensureAnonId` has run, where
 * storage is async). Intended for synchronous, non-critical derivations like the experiment bucket.
 */
export function readAnonIdSync(): string | null {
  if (cachedAnonId) {
    return cachedAnonId
  }
  try {
    const stored = localStorage.getItem(ANON_ID_KEY)
    if (stored) {
      cachedAnonId = stored
      return stored
    }
  } catch {
    // storage unavailable
  }
  return null
}

/**
 * Returns a stable anonymous id, creating and persisting one on first use.
 *
 * Never rotated — the same device keeps its anon id across sessions and after login.
 * That stability is what lets the backend stitch anonymous activity to an account
 * (authenticated events also carry this anon id, and the server derives anon -> eater).
 */
export async function ensureAnonId(): Promise<string> {
  if (cachedAnonId) {
    return cachedAnonId
  }
  const existing = await readStored()
  if (existing) {
    cachedAnonId = existing
    return existing
  }
  const id = generateId()
  cachedAnonId = id
  await writeStored(id)
  return id
}
